import { z } from "zod";

import {
  AUDIO_EXTENSIONS,
  CELL_SIZES,
  LANGUAGES,
  MAX_CELLS,
  MAX_LEN_S,
  MAX_NAME_LEN,
  MAX_VOLUME,
  MIN_LEN_S,
  MIN_VOLUME,
  PHOTO_EXTENSIONS,
} from "./constants";
import { hotkeyIdentity, isReservedHotkey, normalizeHotkey } from "./hotkey";

const SHA256_RE = /^[0-9a-f]{64}$/;

const mediaRef = (extensions: readonly [string, ...string[]]) =>
  z.object({
    sha256: z.string().regex(SHA256_RE, "sha256 must be 64 lowercase hex characters"),
    ext: z.enum(extensions),
  });

const cellSchema = z.object({
  position: z.number().int().min(0),
  name: z
    .string()
    .trim()
    .refine((value) => [...value].length <= MAX_NAME_LEN, {
      message: `name must be at most ${MAX_NAME_LEN} characters`,
    }),
  active: z.boolean(),
  hotkey: z.string().nullable(),
  song_label: z.string().nullable(),
  start_ms: z.number().int().min(0),
  length_s: z.number().int().min(MIN_LEN_S).max(MAX_LEN_S),
  photo: mediaRef(PHOTO_EXTENSIONS).optional(),
  song: mediaRef(AUDIO_EXTENSIONS).optional(),
});

const settingsSchema = z.object({
  volume: z.number().int().min(MIN_VOLUME).max(MAX_VOLUME),
  language: z.enum(LANGUAGES),
  cell_size: z.enum(CELL_SIZES),
});

type CellInput = z.infer<typeof cellSchema>;
type ZodCtx = z.RefinementCtx<unknown>;

function checkHotkey(cell: CellInput, index: number, ctx: ZodCtx): string | null {
  try {
    const canonical = normalizeHotkey(cell.hotkey);
    if (canonical !== null && isReservedHotkey(canonical)) {
      ctx.addIssue({
        code: "custom",
        message: `hotkey ${canonical} is reserved`,
        path: ["cells", index, "hotkey"],
      });
    }
    return canonical;
  } catch (error) {
    ctx.addIssue({
      code: "custom",
      message: error instanceof Error ? error.message : "invalid hotkey",
      path: ["cells", index, "hotkey"],
    });
    return null;
  }
}

function checkCells(cells: readonly CellInput[], ctx: ZodCtx): void {
  const positions = new Set<number>();
  const hotkeys = new Set<string>();
  cells.forEach((cell, index) => {
    if (positions.has(cell.position)) {
      ctx.addIssue({
        code: "custom",
        message: `duplicate position ${cell.position}`,
        path: ["cells", index, "position"],
      });
    }
    positions.add(cell.position);
    const canonical = checkHotkey(cell, index, ctx);
    if (canonical === null) return;
    const identity = hotkeyIdentity(canonical);
    if (hotkeys.has(identity)) {
      ctx.addIssue({
        code: "custom",
        message: `hotkey ${canonical} is already used`,
        path: ["cells", index, "hotkey"],
      });
    }
    hotkeys.add(identity);
  });
}

/** Input accepted by writeBundle/buildManifest; mirrors the desktop manifest limits. */
export const bundleInputSchema = z
  .object({
    settings: settingsSchema,
    cells: z.array(cellSchema).max(MAX_CELLS),
  })
  .superRefine((input, ctx) => checkCells(input.cells, ctx));

export type BundleInput = z.input<typeof bundleInputSchema>;
export type BundleCell = BundleInput["cells"][number];

/** Validate untrusted input at the boundary; throws a ZodError with the failing paths. */
export function parseBundleInput(input: unknown): BundleInput {
  return bundleInputSchema.parse(input);
}
